import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
    AlertTriangle,
    Clock,
    CheckCircle,
    XCircle,
    MessageSquare,
    FileText,
    User,
    DollarSign,
    ChevronRight,
    Plus,
    RefreshCw,
    Eye,
    Send,
    Flag,
    Shield,
    History,
    Package,
    ArrowRight,
    Filter,
    Loader2
} from 'lucide-react';

const statusColors = {
    pending: 'bg-orange-100 text-orange-800 border-orange-200',
    investigating: 'bg-blue-100 text-blue-800 border-blue-200',
    waiting_response: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    escalated: 'bg-red-100 text-red-800 border-red-200',
    resolved: 'bg-green-100 text-green-800 border-green-200',
    closed: 'bg-gray-100 text-gray-800 border-gray-200'
};

const statusLabels = {
    pending: 'În așteptare',
    investigating: 'În investigare',
    waiting_response: 'Așteaptă răspuns',
    escalated: 'Escaladat',
    resolved: 'Rezolvat',
    closed: 'Închis'
};

const resolutionOptions = [
    { value: 'release_to_seller', label: 'Eliberează fonduri către vânzător' },
    { value: 'refund_buyer', label: 'Rambursează cumpărătorul' },
    { value: 'partial_refund', label: 'Rambursare parțială' },
    { value: 'no_action', label: 'Fără acțiune necesară' },
    { value: 'warning_issued', label: 'Avertisment emis' },
    { value: 'user_banned', label: 'Utilizator banat' }
];

const TimelineEntry = ({ entry }) => {
    const getIcon = () => {
        switch (entry.action) {
            case 'status_changed': return <RefreshCw className="w-4 h-4" />;
            case 'evidence_added': return <FileText className="w-4 h-4" />;
            case 'note_added': return <MessageSquare className="w-4 h-4" />;
            case 'resolved': return <CheckCircle className="w-4 h-4" />;
            case 'contacted_buyer': 
            case 'contacted_seller': return <Send className="w-4 h-4" />;
            default: return <Clock className="w-4 h-4" />;
        }
    };

    return (
        <div className="flex gap-3 pb-4">
            <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    {getIcon()}
                </div>
                <div className="w-px h-full bg-border mt-2" />
            </div>
            <div className="flex-1 pb-2">
                <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{entry.action.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{entry.description}</p>
                {(entry.admin_name || entry.admin_username) && (
                    <p className="text-xs text-muted-foreground mt-1">de {entry.admin_name || entry.admin_username}</p>
                )}
            </div>
        </div>
    );
};

const DisputeCard = ({ dispute, onViewDetails, onQuickResolve }) => {
    return (
        <div 
            className="p-4 border rounded-xl hover:bg-muted/30 transition-colors cursor-pointer"
            onClick={() => onViewDetails(dispute)}
            data-testid={`dispute-card-${dispute.report_id}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                        <Badge className={`${statusColors[dispute.status]} border`}>
                            {statusLabels[dispute.status] || dispute.status}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                            {dispute.report_type === 'order' ? 'Comandă' : dispute.report_type === 'fraud' ? 'Fraudă' : dispute.report_type}
                        </Badge>
                    </div>
                    <p className="font-medium truncate">{dispute.reason || 'Fără motiv specificat'}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{dispute.description}</p>
                    
                    {/* Target info - listing, order, or user */}
                    {(dispute.target_title || dispute.listing_title) && (
                        <div className="text-sm mt-2 flex items-center gap-1">
                            <Package className="w-3 h-3 text-primary" />
                            {(dispute.listing_slug || dispute.listing_info?.slug || dispute.listing_info?.listing_id || dispute.target_id) ? (
                                <Link 
                                    to={`/listing/${dispute.listing_slug || dispute.listing_info?.slug || dispute.listing_info?.listing_id || dispute.target_id}`}
                                    className="font-medium text-primary hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid="dispute-listing-link"
                                >
                                    {dispute.target_title || dispute.listing_title}
                                </Link>
                            ) : (
                                <span className="font-medium">{dispute.target_title || dispute.listing_title}</span>
                            )}
                        </div>
                    )}
                    {dispute.order_info && !dispute.target_title && !dispute.listing_title && (
                        <div className="text-sm mt-2 flex items-center gap-1">
                            <Package className="w-3 h-3 text-primary" />
                            {(dispute.order_info.listing_slug || dispute.order_info.listing_id) ? (
                                <Link 
                                    to={`/listing/${dispute.order_info.listing_slug || dispute.order_info.listing_id}`}
                                    className="font-medium text-primary hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid="dispute-order-listing-link"
                                >
                                    {dispute.order_info.listing_title || 'Comandă'}
                                </Link>
                            ) : (
                                <span className="font-medium">{dispute.order_info.listing_title || 'Comandă'}</span>
                            )}
                            <span className="text-muted-foreground">•</span>
                            <Link 
                                to={`/order/${dispute.order_info.order_id}`}
                                className="text-green-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                                data-testid="dispute-order-link"
                            >
                                {formatCurrency(dispute.order_info.amount)}
                            </Link>
                        </div>
                    )}
                    {dispute.order_info && (dispute.target_title || dispute.listing_title) && (
                        <div className="text-sm mt-1 flex items-center gap-1">
                            <DollarSign className="w-3 h-3 text-green-600" />
                            <Link 
                                to={`/order/${dispute.order_info.order_id}`}
                                className="text-green-600 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                                data-testid="dispute-order-amount-link"
                            >
                                Comandă: {formatCurrency(dispute.order_info.amount)}
                            </Link>
                        </div>
                    )}
                    {dispute.target_username && (
                        <p className="text-sm mt-1 flex items-center gap-1">
                            <User className="w-3 h-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Utilizator raportat:</span>
                            <span className="font-medium">{dispute.target_username}</span>
                        </p>
                    )}
                    
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Raportat de: {dispute.reporter_username}
                        </span>
                        {dispute.order_info && (
                            <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {formatCurrency(dispute.order_info.amount)}
                            </span>
                        )}
                        <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(dispute.created_at)}
                        </span>
                    </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    {dispute.status === 'pending' && (
                        <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onQuickResolve(dispute);
                            }}
                        >
                            Rezolvă
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const AdminDisputesTab = ({ onRefresh }) => {
    const [disputes, setDisputes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedDispute, setSelectedDispute] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Infinite scroll
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const perPage = 20;
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);
    
    // Form states
    const [resolution, setResolution] = useState('');
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [newStatus, setNewStatus] = useState('');
    const [statusNotes, setStatusNotes] = useState('');
    const [noteContent, setNoteContent] = useState('');
    
    // Dispute details state
    const [disputeDetails, setDisputeDetails] = useState(null);

    // Fetch disputes
    const fetchDisputes = useCallback(async (pageNum = 1, reset = false) => {
        try {
            if (pageNum === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }

            const params = new URLSearchParams();
            params.append('skip', ((pageNum - 1) * perPage).toString());
            params.append('limit', perPage.toString());
            
            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }

            const response = await api.get(`/admin/disputes?${params.toString()}`);
            const newDisputes = response.data?.disputes || response.data || [];
            const total = response.data?.total || newDisputes.length;

            if (reset || pageNum === 1) {
                setDisputes(newDisputes);
            } else {
                setDisputes(prev => [...prev, ...newDisputes]);
            }

            setTotalCount(total);
            setHasMore(newDisputes.length === perPage);

        } catch (error) {
            console.error('Error fetching disputes:', error);
            toast.error('Eroare la încărcarea disputelor');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [statusFilter]);

    // Reset and fetch when filter changes
    useEffect(() => {
        setPage(1);
        fetchDisputes(1, true);
    }, [statusFilter]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    setPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }

        observerRef.current = observer;

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, loadingMore, loading]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            fetchDisputes(page, false);
        }
    }, [page]);

    const filteredDisputes = disputes;
    const pendingCount = disputes.filter(d => d.status === 'pending').length;
    const investigatingCount = disputes.filter(d => d.status === 'investigating').length;

    const handleViewDetails = async (dispute) => {
        setSelectedDispute(dispute);
        setDetailsOpen(true);
        
        try {
            const res = await api.get(`/admin/disputes/${dispute.report_id}`);
            setDisputeDetails(res.data);
        } catch (error) {
            console.error('Error fetching dispute details:', error);
            setDisputeDetails(dispute);
        }
    };

    const handleQuickResolve = (dispute) => {
        setSelectedDispute(dispute);
        setResolveOpen(true);
    };

    const handleResolve = async () => {
        if (!resolution) {
            toast.error('Selectează o rezoluție');
            return;
        }
        
        setActionLoading(true);
        try {
            await api.post(`/admin/disputes/${selectedDispute.report_id}/resolve`, {
                resolution,
                notes: resolutionNotes
            });
            toast.success('Disputa a fost rezolvată');
            setResolveOpen(false);
            setDetailsOpen(false);
            setResolution('');
            setResolutionNotes('');
            fetchDisputes(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error('Eroare la rezolvarea disputei');
        } finally {
            setActionLoading(false);
        }
    };

    const handleStatusChange = async () => {
        if (!newStatus) {
            toast.error('Selectează un status');
            return;
        }
        
        setActionLoading(true);
        try {
            await api.post(`/admin/disputes/${selectedDispute.report_id}/status`, {
                status: newStatus,
                notes: statusNotes
            });
            toast.success('Statusul a fost actualizat');
            setNewStatus('');
            setStatusNotes('');
            
            // Refresh details
            const res = await api.get(`/admin/disputes/${selectedDispute.report_id}`);
            setDisputeDetails(res.data);
            fetchDisputes(1, true);
            onRefresh?.();
        } catch (error) {
            toast.error('Eroare la actualizarea statusului');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!noteContent.trim()) {
            toast.error('Introdu o notă');
            return;
        }
        
        setActionLoading(true);
        try {
            await api.post(`/admin/disputes/${selectedDispute.report_id}/timeline`, {
                action: 'note_added',
                description: noteContent
            });
            toast.success('Nota a fost adăugată');
            setNoteContent('');
            setAddNoteOpen(false);
            
            // Refresh details
            const res = await api.get(`/admin/disputes/${selectedDispute.report_id}`);
            setDisputeDetails(res.data);
        } catch (error) {
            toast.error('Eroare la adăugarea notei');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-disputes-tab">
            {/* Header with filters */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                Dispute & Rapoarte
                            </CardTitle>
                            <CardDescription>
                                {pendingCount} în așteptare, {investigatingCount} în investigare
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-[160px]">
                                    <Filter className="w-4 h-4 mr-2" />
                                    <SelectValue placeholder="Filtrează" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toate</SelectItem>
                                    <SelectItem value="pending">În așteptare</SelectItem>
                                    <SelectItem value="investigating">În investigare</SelectItem>
                                    <SelectItem value="waiting_response">Așteaptă răspuns</SelectItem>
                                    <SelectItem value="escalated">Escaladat</SelectItem>
                                    <SelectItem value="resolved">Rezolvate</SelectItem>
                                </SelectContent>
                            </Select>
                            <Button variant="outline" size="icon" onClick={onRefresh}>
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {filteredDisputes.length === 0 ? (
                        <div className="text-center py-12">
                            <Shield className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                            <p className="text-muted-foreground">Nu există dispute {statusFilter !== 'all' ? 'cu acest status' : ''}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredDisputes.map((dispute) => (
                                <DisputeCard
                                    key={dispute.report_id}
                                    dispute={dispute}
                                    onViewDetails={handleViewDetails}
                                    onQuickResolve={handleQuickResolve}
                                />
                            ))}
                            
                            {/* Infinite Scroll Loader */}
                            {hasMore && (
                                <div ref={loadMoreRef} className="flex justify-center py-4">
                                    {loadingMore && (
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>Se încarcă...</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {!hasMore && disputes.length > 0 && (
                                <p className="text-center text-sm text-muted-foreground py-4">
                                    Ai ajuns la final ({totalCount} dispute)
                                </p>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dispute Details Dialog */}
            <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Flag className="w-5 h-5" />
                            Detalii Dispută
                        </DialogTitle>
                        {selectedDispute && (
                            <Badge className={`${statusColors[selectedDispute.status]} border w-fit`}>
                                {statusLabels[selectedDispute.status] || selectedDispute.status}
                            </Badge>
                        )}
                    </DialogHeader>
                    
                    <ScrollArea className="flex-1 pr-4">
                        {disputeDetails && (
                            <Tabs defaultValue="details" className="w-full">
                                <TabsList className="mb-4">
                                    <TabsTrigger value="details">Detalii</TabsTrigger>
                                    <TabsTrigger value="timeline">Timeline ({disputeDetails.timeline?.length || 0})</TabsTrigger>
                                    <TabsTrigger value="order">Comandă</TabsTrigger>
                                </TabsList>
                                
                                <TabsContent value="details" className="space-y-4">
                                    {/* Main Info */}
                                    <div className="grid gap-4">
                                        <div className="p-4 rounded-lg bg-muted/30">
                                            <Label className="text-xs text-muted-foreground">Motiv</Label>
                                            <p className="font-medium">{disputeDetails.reason || 'Nespecificat'}</p>
                                        </div>
                                        <div className="p-4 rounded-lg bg-muted/30">
                                            <Label className="text-xs text-muted-foreground">Descriere</Label>
                                            <p className="text-sm">{disputeDetails.description || 'Fără descriere'}</p>
                                        </div>
                                    </div>
                                    
                                    {/* Parties */}
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="p-4 rounded-lg border">
                                            <Label className="text-xs text-muted-foreground mb-2 block">Raportat de</Label>
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                    <User className="w-5 h-5 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="font-medium">{disputeDetails.reporter_info?.username || disputeDetails.reporter_username}</p>
                                                    <p className="text-xs text-muted-foreground">{disputeDetails.reporter_info?.email || disputeDetails.reporter_email}</p>
                                                </div>
                                            </div>
                                        </div>
                                        {disputeDetails.target_info && (
                                            <div className="p-4 rounded-lg border">
                                                <Label className="text-xs text-muted-foreground mb-2 block">Utilizator raportat</Label>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                                        <User className="w-5 h-5 text-red-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{disputeDetails.target_info?.username || disputeDetails.target_username}</p>
                                                        <p className="text-xs text-muted-foreground">{disputeDetails.target_info?.email || disputeDetails.target_email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Status Change */}
                                    {disputeDetails.status !== 'resolved' && disputeDetails.status !== 'closed' && (
                                        <div className="p-4 rounded-lg border space-y-3">
                                            <Label>Schimbă Status</Label>
                                            <div className="flex gap-2">
                                                <Select value={newStatus} onValueChange={setNewStatus}>
                                                    <SelectTrigger className="flex-1">
                                                        <SelectValue placeholder="Selectează status" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="investigating">În investigare</SelectItem>
                                                        <SelectItem value="waiting_response">Așteaptă răspuns</SelectItem>
                                                        <SelectItem value="escalated">Escaladat</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Button onClick={handleStatusChange} disabled={!newStatus || actionLoading}>
                                                    Actualizează
                                                </Button>
                                            </div>
                                            <Textarea
                                                placeholder="Note pentru schimbarea statusului..."
                                                value={statusNotes}
                                                onChange={(e) => setStatusNotes(e.target.value)}
                                                className="h-20"
                                            />
                                        </div>
                                    )}
                                </TabsContent>
                                
                                <TabsContent value="timeline" className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-medium">Istoric Acțiuni</h4>
                                        <Button size="sm" variant="outline" onClick={() => setAddNoteOpen(true)}>
                                            <Plus className="w-4 h-4 mr-2" />
                                            Adaugă notă
                                        </Button>
                                    </div>
                                    
                                    {disputeDetails.timeline?.length > 0 ? (
                                        <div className="space-y-1">
                                            {disputeDetails.timeline.map((entry, i) => (
                                                <TimelineEntry key={i} entry={entry} />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-center text-muted-foreground py-8">
                                            Nu există înregistrări în timeline
                                        </p>
                                    )}
                                </TabsContent>
                                
                                <TabsContent value="order" className="space-y-4">
                                    {disputeDetails.order ? (
                                        <div className="space-y-4">
                                            <div className="p-4 rounded-lg bg-muted/30">
                                                <div className="flex items-center justify-between mb-3">
                                                    <Label className="text-xs text-muted-foreground">Comandă</Label>
                                                    <Badge variant="outline">
                                                        #{disputeDetails.order.order_id?.slice(-8)}
                                                    </Badge>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-muted-foreground">Sumă:</span>
                                                        <span className="font-bold ml-2">{formatCurrency(disputeDetails.order.amount)}</span>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Status Escrow:</span>
                                                        <Badge className="ml-2" variant="outline">{disputeDetails.order.escrow_status}</Badge>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Status Comandă:</span>
                                                        <Badge className="ml-2" variant="outline">{disputeDetails.order.order_status}</Badge>
                                                    </div>
                                                    <div>
                                                        <span className="text-muted-foreground">Creat:</span>
                                                        <span className="ml-2">{formatDate(disputeDetails.order.created_at)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {disputeDetails.listing && (
                                                <div className="p-4 rounded-lg border">
                                                    <Label className="text-xs text-muted-foreground mb-2 block">Produs</Label>
                                                    <div className="flex items-center gap-3">
                                                        {disputeDetails.listing.images?.[0] && (
                                                            <img 
                                                                src={disputeDetails.listing.images[0]} 
                                                                alt="" 
                                                                className="w-16 h-16 rounded-lg object-cover"
                                                            />
                                                        )}
                                                        <div>
                                                            <p className="font-medium">{disputeDetails.listing.title}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {disputeDetails.listing.category_name}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-center text-muted-foreground py-8">
                                            Nu este asociată nicio comandă
                                        </p>
                                    )}
                                </TabsContent>
                            </Tabs>
                        )}
                    </ScrollArea>
                    
                    <DialogFooter className="border-t pt-4">
                        <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                            Închide
                        </Button>
                        {selectedDispute?.status !== 'resolved' && selectedDispute?.status !== 'closed' && (
                            <Button onClick={() => {
                                setResolveOpen(true);
                            }}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Rezolvă Disputa
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Resolve Dialog */}
            <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rezolvă Disputa</DialogTitle>
                        <DialogDescription>
                            Selectează rezoluția și adaugă note explicative
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div>
                            <Label>Rezoluție</Label>
                            <Select value={resolution} onValueChange={setResolution}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Selectează rezoluția" />
                                </SelectTrigger>
                                <SelectContent>
                                    {resolutionOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div>
                            <Label>Note</Label>
                            <Textarea
                                placeholder="Explică rezoluția..."
                                value={resolutionNotes}
                                onChange={(e) => setResolutionNotes(e.target.value)}
                                className="h-24"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResolveOpen(false)}>
                            Anulează
                        </Button>
                        <Button onClick={handleResolve} disabled={loading || !resolution}>
                            {loading ? 'Se procesează...' : 'Confirmă Rezoluția'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Note Dialog */}
            <Dialog open={addNoteOpen} onOpenChange={setAddNoteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Adaugă Notă</DialogTitle>
                    </DialogHeader>
                    
                    <div>
                        <Label>Conținut</Label>
                        <Textarea
                            placeholder="Scrie nota aici..."
                            value={noteContent}
                            onChange={(e) => setNoteContent(e.target.value)}
                            className="h-32"
                        />
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddNoteOpen(false)}>
                            Anulează
                        </Button>
                        <Button onClick={handleAddNote} disabled={loading || !noteContent.trim()}>
                            Adaugă
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDisputesTab;
